/**
 * **The Odżywianie tab's lower half** — the „Tygodnie" list, the chosen week's table, and the A/B
 * comparison of two weeks. All three read the 12-week range (R10: Monday-to-Sunday weeks with their
 * own "X of Y" and averages), so the list and the pane cannot disagree about a week.
 *
 * The comparison's colours are the mockup's rule: a change TOWARD the target is green and a change
 * away from it red, and the colour never contradicts the arrow. Days on target and days logged read
 * „more is better"; protein reads „more is
 * better" (its target is a floor, R9); sugar reads „less is better"; energy, carbohydrate and fat read
 * „closer to the current target is better", and with no target for that nutrient the change is
 * uncoloured.
 */

import { dateTimeFormat } from '~/shared/lib/intlCache';
import { capitaliseFirst } from '~/shared/lib/localeCase';
import { parseIsoDate } from '~/shared/lib/calendarDate';

import {
  atLeast,
  dayMonth,
  isLogged,
  isPartial,
  signedWhole,
  targetOf,
  toldStatus,
  unitOf,
  weekSpanLabel,
  wholeNumber,
  withUnit,
  NUTRIENTS,
  type Nutrient,
  type NutritionDay,
  type NutritionTargetValues,
  type WeekBundle,
} from './nutrition';
import { missText, type NutritionWords } from './nutritionStage';

const KEY = 'web:clientDetail.nutrition';

export type ChipTone = 'good' | 'bad' | 'neutral';

export interface NutritionWeekRow {
  readonly key: string;
  /** Position in the list, newest first. */
  readonly index: number;
  readonly title: string;
  readonly subtitle: string;
  readonly chip: { readonly text: string; readonly tone: ChipTone };
}

export interface NutritionWeekMonth {
  readonly key: string;
  readonly label: string;
  readonly rows: readonly NutritionWeekRow[];
}

export function weekChip(
  bundle: WeekBundle,
  hasTarget: boolean,
  words: NutritionWords,
): { text: string; tone: ChipTone } {
  const { t } = words;
  const { judgedDays, onTargetDays } = bundle.week.summary;
  if (!hasTarget) {
    return {
      text: t(`${KEY}.chipLogged`, {
        logged: bundle.days.filter(isLogged).length,
        days: bundle.days.length,
      }),
      tone: 'neutral',
    };
  }
  if (judgedDays === 0) return { text: t(`${KEY}.chipUnjudged`), tone: 'neutral' };
  const tone: ChipTone =
    onTargetDays === judgedDays ? 'good' : onTargetDays / judgedDays < 0.5 ? 'bad' : 'neutral';
  return { text: t(`${KEY}.chipOnTarget`, { ok: onTargetDays, judged: judgedDays }), tone };
}

/** Twelve weeks with nothing logged have nothing to list or open, so the tab leaves them out. */
export function anyWeekLogged(bundles: readonly WeekBundle[]): boolean {
  return bundles.some((bundle) => bundle.days.some(isLogged));
}

export function buildWeekList(
  bundles: readonly WeekBundle[],
  hasTarget: boolean,
  words: NutritionWords,
): NutritionWeekMonth[] {
  const { t, locale } = words;
  const monthName = dateTimeFormat(locale, { month: 'long' });
  const months: { key: string; label: string; rows: NutritionWeekRow[] }[] = [];
  bundles.forEach((bundle, index) => {
    const start = parseIsoDate(bundle.week.weekStart);
    const monthKey = start === null ? bundle.week.weekStart : `${String(start.getFullYear())}-${String(start.getMonth())}`;
    let month = months[months.length - 1];
    if (month === undefined || month.key !== monthKey) {
      month = { key: monthKey, label: start === null ? '' : monthName.format(start), rows: [] };
      months.push(month);
    }
    const span = weekSpanLabel(bundle.week.weekStart, bundle.week.weekEnd, locale);
    const energy = bundle.week.summary.averages.energy;
    const average =
      energy !== null
        ? t(`${KEY}.weekAverage`, { value: wholeNumber(energy, locale) })
        : bundle.current && bundle.days.some((day) => day.isToday && isLogged(day))
          ? t(`${KEY}.weekTodayInProgress`)
          : t(`${KEY}.weekNoEntries`);
    month.rows.push({
      key: bundle.week.weekStart,
      index,
      title: bundle.current ? t(`${KEY}.thisWeek`) : span,
      subtitle: bundle.current ? `${span} · ${average}` : average,
      chip: weekChip(bundle, hasTarget, words),
    });
  });
  return months;
}

/* ── the week pane ───────────────────────────────────────────────────────────────────────────── */

export type DayStatusKind = 'ok' | 'off' | 'none' | 'progress' | 'unjudged';

export interface NutritionDayStatusView {
  readonly kind: DayStatusKind;
  readonly text: string;
  /** „i 2 więcej", „1 240 kcal do celu". */
  readonly more: string | null;
  /** Every reason, for the cell's title. */
  readonly all: string | null;
  /** In progress: the share of the energy target eaten so far, 0–1. */
  readonly share: number;
}

export interface NutritionDayCell {
  readonly nutrient: Nutrient;
  readonly value: string | null;
  readonly unit: 'kcal' | 'g';
  readonly off: boolean;
  /** Some entry left this nutrient out; the value is printed as a floor (R8). */
  readonly partial: boolean;
}

export interface NutritionDayRow {
  readonly date: string;
  readonly dayName: string;
  readonly dateLabel: string;
  /** „5 posiłków", shown beside the date of the opened day; null with no meal. */
  readonly meals: string | null;
  readonly today: boolean;
  readonly logged: boolean;
  readonly cells: readonly NutritionDayCell[];
  readonly sugar: string | null;
  readonly sugarPartial: boolean;
  readonly status: NutritionDayStatusView;
}

export interface NutritionTile {
  readonly nutrient: Nutrient;
  readonly value: string | null;
  readonly unit: 'kcal' | 'g';
  readonly note: string;
}

export interface NutritionWeekPane {
  readonly title: string;
  readonly caption: string;
  readonly tiles: readonly NutritionTile[];
  readonly rows: readonly NutritionDayRow[];
}

export function dayStatusView(day: NutritionDay, words: NutritionWords): NutritionDayStatusView {
  const { t, locale } = words;
  const none = { more: null, all: null, share: 0 };
  switch (toldStatus(day)) {
    case 'onTarget':
      return { kind: 'ok', text: t(`${KEY}.status.onTarget`), ...none };
    case 'offTarget': {
      const texts = day.reasons.map((miss) => missText(miss, words));
      return {
        kind: 'off',
        text: texts[0] ?? t(`${KEY}.status.offTarget`),
        more: texts.length > 1 ? t(`${KEY}.moreReasons`, { count: texts.length - 1 }) : null,
        all: texts.length > 0 ? texts.join(', ') : null,
        share: 0,
      };
    }
    case 'inProgress': {
      const eaten = day.totals.energy ?? 0;
      const goal = day.target?.energy ?? null;
      return {
        kind: 'progress',
        text: t(`${KEY}.status.inProgress`),
        more:
          goal === null
            ? null
            : eaten > goal
              ? t(`${KEY}.overTarget`, { value: wholeNumber(eaten - goal, locale) })
              : t(`${KEY}.leftToTarget`, { value: wholeNumber(goal - eaten, locale) }),
        all: null,
        share: goal === null || goal === 0 ? 0 : Math.min(1, eaten / goal),
      };
    }
    case 'notLogged':
      return { kind: 'none', text: t(`${KEY}.status.notLogged`), ...none };
    case 'noTarget':
      return { kind: 'unjudged', text: t(`${KEY}.status.noTarget`), ...none };
  }
}

export function buildWeekPane(bundle: WeekBundle, words: NutritionWords): NutritionWeekPane {
  const { t, locale } = words;
  const { summary } = bundle.week;
  const span = weekSpanLabel(bundle.week.weekStart, bundle.week.weekEnd, locale);
  const governing = bundle.week.target;
  const unlogged = bundle.days.filter((day) => !day.isToday && !isLogged(day)).length;
  const logged = bundle.days.filter(isLogged).length;

  const captionParts = [
    governing === null
      ? t(`${KEY}.weekLogged`, { logged, count: bundle.days.length })
      : summary.judgedDays === 0
        ? t(`${KEY}.weekUnjudged`)
        : t(`${KEY}.onTargetDays`, { ok: summary.onTargetDays, count: summary.judgedDays }),
  ];
  if (unlogged > 0) captionParts.push(t(`${KEY}.unloggedDaysShort`, { count: unlogged }));
  captionParts.push(t(`${KEY}.openDayHint`), t(`${KEY}.readOnlyHint`));

  const weekdayName = dateTimeFormat(locale, { weekday: 'long' });
  const rows = bundle.days.map((day): NutritionDayRow => {
    const parsed = parseIsoDate(day.date);
    return {
      date: day.date,
      dayName: parsed === null ? day.date : capitaliseFirst(weekdayName.format(parsed), locale),
      dateLabel: dayMonth(day.date, locale),
      meals: day.mealCount > 0 ? t(`${KEY}.mealCount`, { count: day.mealCount }) : null,
      today: day.isToday,
      logged: isLogged(day),
      cells: NUTRIENTS.map((nutrient) => {
        const value = day.totals[nutrient];
        const partial = value !== null && isPartial(day, nutrient);
        return {
          nutrient,
          value: value === null ? null : atLeast(wholeNumber(value, locale), partial),
          unit: unitOf(nutrient),
          off: day.verdicts[nutrient] === 'warn',
          partial,
        };
      }),
      sugar:
        day.totals.sugar === null || (day.partial.sugar && day.totals.sugar === 0)
          ? null
          : atLeast(wholeNumber(day.totals.sugar, locale), day.partial.sugar),
      sugarPartial: day.totals.sugar !== null && day.totals.sugar > 0 && day.partial.sugar,
      status: dayStatusView(day, words),
    };
  });

  return {
    title: t(bundle.current ? `${KEY}.thisWeekTitle` : `${KEY}.weekTitle`, { span }),
    caption: captionParts.join(' · '),
    tiles: NUTRIENTS.map((nutrient) => tile(nutrient, bundle, governing, words)),
    rows,
  };
}

function tile(
  nutrient: Nutrient,
  bundle: WeekBundle,
  governing: NutritionTargetValues | null,
  words: NutritionWords,
): NutritionTile {
  const { t, locale } = words;
  const unit = unitOf(nutrient);
  const value = bundle.week.summary.averages[nutrient];
  const goal = targetOf(governing, nutrient);
  let note =
    goal === null
      ? t(`${KEY}.tileNoTarget`)
      : t(`${KEY}.tileTarget`, { value: withUnit(goal, unit, locale) });
  if (nutrient === 'carbs') {
    const sugar = bundle.week.summary.averages.sugar;
    note = `${note} · ${t(`${KEY}.tileSugar`, { value: sugar === null ? '—' : withUnit(sugar, 'g', locale) })}`;
  }
  return { nutrient, value: value === null ? null : wholeNumber(value, locale), unit, note };
}

/* ── the comparison ──────────────────────────────────────────────────────────────────────────── */

export type CompareQuantity = 'onTarget' | Nutrient | 'sugar' | 'logged';

export interface NutritionCompareRow {
  readonly key: CompareQuantity;
  readonly label: string;
  /** A sub-row of the one above it — sugar under carbohydrate. */
  readonly sub: boolean;
  readonly change: { readonly text: string; readonly sign: number; readonly tone: ChipTone } | null;
  readonly a: string;
  readonly b: string;
}

export interface NutritionCompareView {
  readonly aTitle: string;
  readonly aSpan: string;
  readonly bTitle: string;
  readonly bSpan: string;
  readonly rows: readonly NutritionCompareRow[];
}

const COMPARE_ROWS: readonly CompareQuantity[] = [
  'onTarget',
  'energy',
  'protein',
  'carbs',
  'sugar',
  'fat',
  'logged',
];

type CountQuantity = Extract<CompareQuantity, 'onTarget' | 'logged'>;

function isCount(key: CompareQuantity): key is CountQuantity {
  return key === 'onTarget' || key === 'logged';
}

function quantityOf(bundle: WeekBundle, key: CompareQuantity): number | null {
  if (key === 'onTarget') return bundle.week.summary.onTargetDays;
  if (key === 'logged') return bundle.days.filter(isLogged).length;
  return bundle.week.summary.averages[key];
}

/** The days a count is out of: the judged days for „w celu", every day of the week for „z wpisami". */
function denominatorOf(bundle: WeekBundle, key: CountQuantity): number {
  return key === 'onTarget' ? bundle.week.summary.judgedDays : bundle.days.length;
}

/** Which way a change from A to B reads; the colour never contradicts the arrow. */
export function changeTone(
  key: CompareQuantity,
  a: number,
  b: number,
  target: NutritionTargetValues | null,
): ChipTone {
  const delta = b - a;
  if (delta === 0) return 'neutral';
  if (isCount(key) || key === 'protein') return delta > 0 ? 'good' : 'bad';
  if (key === 'sugar') return delta < 0 ? 'good' : 'bad';
  const goal = targetOf(target, key);
  if (goal === null) return 'neutral';
  const before = Math.abs(a - goal);
  const after = Math.abs(b - goal);
  return after < before ? 'good' : after > before ? 'bad' : 'neutral';
}

export function buildWeekCompare(
  a: WeekBundle,
  b: WeekBundle,
  target: NutritionTargetValues | null,
  words: NutritionWords,
): NutritionCompareView {
  const { t, locale } = words;
  const figure = (key: CompareQuantity, bundle: WeekBundle): string => {
    const value = quantityOf(bundle, key);
    if (isCount(key)) {
      return t(`${KEY}.compareOf`, { value: value ?? 0, of: denominatorOf(bundle, key) });
    }
    return value === null ? '—' : withUnit(value, unitOf(key), locale);
  };
  const rows = COMPARE_ROWS.map((key): NutritionCompareRow => {
    const before = quantityOf(a, key);
    const after = quantityOf(b, key);
    let change: NutritionCompareRow['change'] = null;
    if (before !== null && after !== null) {
      const delta = after - before;
      const signed = signedWhole(delta, locale);
      change =
        delta === 0
          ? { text: t(`${KEY}.noChange`), sign: 0, tone: 'neutral' }
          : {
              text: isCount(key) ? signed : `${signed}\u00a0${unitOf(key)}`,
              sign: Math.sign(delta),
              tone: changeTone(key, before, after, target),
            };
    }
    return {
      key,
      label: t(`${KEY}.compareRow.${key}`),
      sub: key === 'sugar',
      change,
      a: figure(key, a),
      b: figure(key, b),
    };
  });
  const span = (bundle: WeekBundle) =>
    weekSpanLabel(bundle.week.weekStart, bundle.week.weekEnd, locale);
  const title = (bundle: WeekBundle) => (bundle.current ? t(`${KEY}.thisWeek`) : span(bundle));
  return {
    aTitle: t(`${KEY}.compareColumn`, { side: 'A', title: title(a) }),
    aSpan: span(a),
    bTitle: t(`${KEY}.compareColumn`, { side: 'B', title: title(b) }),
    bSpan: span(b),
    rows,
  };
}

/** The next A/B pair after the trainer ticks `index`: the older pick drops off (the mockup's rule). */
export function nextComparePick(
  pick: readonly [number, number],
  index: number,
): readonly [number, number] {
  if (pick.includes(index)) return pick;
  return [pick[1], index];
}
